import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Film, LayoutGrid, Layers, List, MoreVertical, Music, Plus, Save, Search, Star, Trash2, Tv } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { api, type Assignment, type Category, type Channel, type LineupEntry, mediaUrl, type Package } from '../api/client';
import { useSession, useSite } from '../app/session';
import { Pager, usePaged } from '../components/Pager';
import { Menu } from '../components/Menu';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Field, Loading, Modal, PageHeader } from '../components/ui';
import { CategoryChip } from './LiveTvPage';
import { ask } from '../components/Confirm';

const usePackages = () => useQuery({ queryKey: ['packages'], queryFn: () => api.get<{ items: Package[] }>('/packages') });
type MediaCat = { id: string; name: string };
const useVodCats = () => useQuery({ queryKey: ['vod', 'categories'], queryFn: () => api.get<{ items: MediaCat[] }>('/vod/categories') });
const useAodCats = () => useQuery({ queryKey: ['aod', 'categories'], queryFn: () => api.get<{ items: MediaCat[] }>('/aod/categories') });
const usePackageMedia = (id: string | undefined) =>
  useQuery({
    queryKey: ['package-media', id],
    queryFn: () => api.get<{ vodCategoryIds: string[]; aodCategoryIds: string[] }>(`/packages/${id}/media`),
    enabled: !!id,
  });

function StatCard({ icon, value, label, sub }: { icon: ReactNode; value: number | string; label: string; sub?: string }) {
  return (
    <div className="stat-tile">
      <span className="stat-tile-icon violet">{icon}</span>
      <span>
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-label">{label}</span>
        {sub && <span className="stat-tile-sub">{sub}</span>}
      </span>
    </div>
  );
}

const codeOf = (name: string) => name.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 32) || 'PACKAGE';

export function PackagesPage() {
  const { can } = useSession();
  const site = useSite();
  const qc = useQueryClient();
  const packages = usePackages();
  const vodCats = useVodCats();
  const aodCats = useAodCats();
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState('');
  const items = packages.data?.items ?? [];
  const current = items.find((p) => p.id === selected) ?? items[0];
  const media = usePackageMedia(current?.id);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['packages'] });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/packages/${id}`), onSuccess: invalidate });
  const makeDefault = useMutation({ mutationFn: (id: string) => api.patch(`/packages/${id}`, { isDefault: true }), onSuccess: invalidate });
  const duplicate = useMutation({
    mutationFn: async (p: Package) => {
      const taken = new Set(items.map((x) => x.code));
      let code = codeOf(`${p.code}_COPY`);
      for (let i = 2; taken.has(code); i++) code = codeOf(`${p.code}_COPY_${i}`);
      const copy = await api.post<Package>('/packages', { name: `${p.name} (copy)`, code, description: p.description ?? undefined });
      await api.put(`/packages/${copy.id}/channels`, { channelIds: p.channels.map((c) => c.channelId) });
      const m = await api.get<{ vodCategoryIds: string[]; aodCategoryIds: string[] }>(`/packages/${p.id}/media`);
      await api.put(`/packages/${copy.id}/media`, m);
      return copy;
    },
    onSuccess: async (copy) => {
      await invalidate();
      setSelected(copy.id);
    },
  });
  const askDelete = (p: Package) => ask(`Delete package ${p.name}? Rooms using it fall back to their next assignment.`, () => remove.mutate(p.id));
  const shown = q ? items.filter((p) => `${p.name} ${p.code}`.toLowerCase().includes(q.toLowerCase())) : items;
  const actions = { duplicate: (p: Package) => duplicate.mutate(p), makeDefault: (p: Package) => makeDefault.mutate(p.id), remove: askDelete };

  return (
    <>
      <PageHeader
        title="Content packages"
        sub="A package is a set of channels, movie and music categories. Assign packages to a site, floor, room or a single device; the most specific one wins."
        actions={
          can('ADMIN') && (
            <button className="btn primary" onClick={() => setCreating(true)}>
              <Plus size={16} /> New package
            </button>
          )
        }
      />
      {packages.isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="stat-strip">
            <StatCard icon={<Layers size={20} />} value={items.length} label="Total packages" />
            <StatCard icon={<Tv size={20} />} value={current?.channels.length ?? 0} label="Selected channels" sub="in this package" />
            <StatCard icon={<Film size={20} />} value={media.data?.vodCategoryIds.length ?? 0} label="VOD categories" sub="selected" />
            <StatCard icon={<Music size={20} />} value={media.data?.aodCategoryIds.length ?? 0} label="AOD categories" sub="selected" />
          </div>
          <div className="pkg-layout">
            <div className="card pkg-list">
              <h2>Packages</h2>
              <label className="search">
                <Search size={16} />
                <input placeholder="Search packages…" value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
              <div className="pkg-items">
                {shown.map((p) => (
                  <div key={p.id} className={`pkg-item${current?.id === p.id ? ' on' : ''}`} onClick={() => setSelected(p.id)}>
                    <span className="pkg-item-text">
                      <span className="pkg-item-name">{p.name}</span>
                      <span className="pkg-item-code">{p.code}</span>
                    </span>
                    {p.isDefault ? <Star size={16} className="pkg-star on" fill="currentColor" /> : <Star size={16} className="pkg-star" />}
                    <span className="pkg-count">{p.channels.length}</span>
                    {can('ADMIN') && <RowMenu pkg={p} actions={actions} />}
                  </div>
                ))}
                {shown.length === 0 && <div className="muted" style={{ padding: 12 }}>No packages.</div>}
              </div>
            </div>
            {current && media.data ? (
              <PackageEditor
                key={current.id}
                pkg={current}
                siteId={site?.id ?? null}
                media={media.data}
                vodCats={vodCats.data?.items ?? []}
                aodCats={aodCats.data?.items ?? []}
                onMakeDefault={() => makeDefault.mutate(current.id)}
                onDuplicate={() => duplicate.mutate(current)}
                onDelete={() => askDelete(current)}
              />
            ) : (
              <div className="card">{current ? <Loading /> : <Empty>Create your first package.</Empty>}</div>
            )}
          </div>
          <ErrorBox error={remove.error ?? duplicate.error ?? makeDefault.error} />
        </>
      )}
      {creating && <CreatePackageModal onClose={() => setCreating(false)} onCreated={(id) => setSelected(id)} />}
    </>
  );
}

function RowMenu({ pkg, actions }: { pkg: Package; actions: { duplicate: (p: Package) => void; makeDefault: (p: Package) => void; remove: (p: Package) => void } }) {
  return (
    <Menu
      minWidth={190}
      button={({ onClick }) => (
        <button type="button" className="icon-btn" title="More" onClick={onClick}>
          <MoreVertical size={16} />
        </button>
      )}
    >
      {!pkg.isDefault && (
        <button type="button" className="more-item" onClick={() => actions.makeDefault(pkg)}>
          <Star size={15} /> Make default
        </button>
      )}
      <button type="button" className="more-item" onClick={() => actions.duplicate(pkg)}>
        <Copy size={15} /> Duplicate
      </button>
      <button type="button" className="more-item danger-item" onClick={() => actions.remove(pkg)}>
        <Trash2 size={15} /> Delete
      </button>
    </Menu>
  );
}

function CreatePackageModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const create = useMutation({
    mutationFn: () => api.post<Package>('/packages', { name, code }),
    onSuccess: async (p) => {
      await qc.invalidateQueries({ queryKey: ['packages'] });
      onCreated(p.id);
      onClose();
    },
  });
  return (
    <Modal
      title="New package"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!name || !code || create.isPending} onClick={() => create.mutate()}>
            Create package
          </button>
        </>
      }
    >
      <Field label="Name *">
        <input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setCode(codeOf(e.target.value));
          }}
          placeholder="e.g. Premium"
          autoFocus
        />
      </Field>
      <Field label="Code *" hint="Capital letters, digits and _; used by integrations.">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
      </Field>
      <ErrorBox error={create.error} />
    </Modal>
  );
}

/** Pick list of chips with a check circle (VOD / AOD categories). */
function ChipPicker({ items, selected, onChange, editable }: { items: MediaCat[]; selected: Set<string>; onChange: (s: Set<string>) => void; editable: boolean }) {
  if (!items.length) return <div className="muted">No categories yet.</div>;
  return (
    <div className="pick-chips">
      {items.map((c) => {
        const on = selected.has(c.id);
        return (
          <button
            key={c.id}
            type="button"
            className={`pick-chip${on ? ' on' : ''}`}
            disabled={!editable}
            onClick={() => {
              const n = new Set(selected);
              if (on) n.delete(c.id);
              else n.add(c.id);
              onChange(n);
            }}
          >
            <span className="pick-dot">{on && <Check size={12} />}</span>
            {c.name}
          </button>
        );
      })}
    </div>
  );
}

function Section({ icon, title, sub, right, children }: { icon: ReactNode; title: string; sub: string; right?: ReactNode; children: ReactNode }) {
  return (
    <div className="pkg-section">
      <div className="pkg-section-head">
        <span className="pkg-section-icon">{icon}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="pkg-section-title">{title}</span>
          <span className="pkg-section-sub">{sub}</span>
        </span>
        {right}
      </div>
      {children}
    </div>
  );
}

function PackageEditor({
  pkg,
  siteId,
  media,
  vodCats,
  aodCats,
  onMakeDefault,
  onDuplicate,
  onDelete,
}: {
  pkg: Package;
  siteId: string | null;
  media: { vodCategoryIds: string[]; aodCategoryIds: string[] };
  vodCats: MediaCat[];
  aodCats: MediaCat[];
  onMakeDefault: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { can } = useSession();
  const qc = useQueryClient();
  const channels = useQuery({ queryKey: ['channels'], queryFn: () => api.get<{ items: Channel[] }>('/channels') });
  const categories = useQuery({ queryKey: ['categories'], queryFn: () => api.get<{ items: Category[] }>('/categories') });
  const lineup = useQuery({
    queryKey: ['lineup', siteId],
    queryFn: () => api.get<{ items: LineupEntry[] }>(`/sites/${siteId}/lineup`),
    enabled: !!siteId,
  });
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(pkg.channels.map((c) => c.channelId)));
  const [vod, setVod] = useState<Set<string>>(() => new Set(media.vodCategoryIds));
  const [aod, setAod] = useState<Set<string>>(() => new Set(media.aodCategoryIds));
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const same = (a: Set<string>, b: string[]) => a.size === b.length && b.every((x) => a.has(x));
  const dirty = !same(chosen, pkg.channels.map((c) => c.channelId)) || !same(vod, media.vodCategoryIds) || !same(aod, media.aodCategoryIds);
  const numberOf = useMemo(() => new Map((lineup.data?.items ?? []).map((e) => [e.channelId, e.number])), [lineup.data]);
  const all = useMemo(
    () => [...(channels.data?.items ?? [])].sort((a, b) => (numberOf.get(a.id) ?? 1e9) - (numberOf.get(b.id) ?? 1e9) || a.name.localeCompare(b.name)),
    [channels.data, numberOf],
  );
  const shown = all.filter((c) => {
    const s = q.trim().toLowerCase();
    if (s && !`${numberOf.get(c.id) ?? ''} ${c.name} ${c.categories.map((x) => x.category.name).join(' ')}`.toLowerCase().includes(s)) return false;
    if (cat === 'selected') return chosen.has(c.id);
    if (cat !== 'all' && !c.categories.some((x) => x.categoryId === cat)) return false;
    return true;
  });
  const { rows, pager } = usePaged(shown, 25);
  const save = useMutation({
    mutationFn: async () => {
      await api.put(`/packages/${pkg.id}/channels`, { channelIds: [...chosen] });
      await api.put(`/packages/${pkg.id}/media`, { vodCategoryIds: [...vod], aodCategoryIds: [...aod] });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['packages'] });
      void qc.invalidateQueries({ queryKey: ['package-media', pkg.id] });
    },
  });
  const editable = can('ADMIN');
  const toggle = (id: string) =>
    setChosen((cur) => {
      const n = new Set(cur);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <div className="card pkg-editor">
      <div className="pkg-head">
        <span className="pkg-head-icon">
          <Layers size={22} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="row" style={{ gap: 8 }}>
            <span className="pkg-head-title">{pkg.name}</span>
            <span className="badge">{pkg.code}</span>
            {pkg.isDefault && (
              <span className="badge brand">
                <Star size={12} fill="currentColor" /> Default package
              </span>
            )}
          </span>
          <span className="muted" style={{ fontSize: 13 }}>
            {pkg.isDefault ? 'Rooms with no package of their own get this one. ' : ''}
            Assigned to {pkg._count.assignments} {pkg._count.assignments === 1 ? 'place' : 'places'} (site, floors, rooms, TVs).
          </span>
        </span>
        {editable && (
          <span className="row">
            {!pkg.isDefault && (
              <button className="btn" onClick={onMakeDefault}>
                <Star size={16} /> Make default
              </button>
            )}
            <button className="btn" onClick={onDuplicate}>
              <Copy size={16} /> Duplicate
            </button>
            <button className="btn danger" onClick={onDelete}>
              <Trash2 size={16} /> Delete
            </button>
            <button className="btn primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              <Save size={16} /> {save.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </span>
        )}
      </div>
      <ErrorBox error={save.error} />

      <Section
        icon={<Film size={18} />}
        title="VOD categories"
        sub="Movie categories included in this package."
        right={
          <span className="row">
            <span className="muted">{vod.size} selected</span>
            {editable && (
              <button className="btn small" onClick={() => setVod(vod.size === vodCats.length ? new Set() : new Set(vodCats.map((c) => c.id)))}>
                {vod.size === vodCats.length && vodCats.length ? 'Clear' : 'Select all'}
              </button>
            )}
          </span>
        }
      >
        <ChipPicker items={vodCats} selected={vod} onChange={setVod} editable={editable} />
      </Section>

      <Section
        icon={<Music size={18} />}
        title="AOD categories"
        sub="Music categories included in this package."
        right={
          <span className="row">
            <span className="muted">{aod.size} selected</span>
            {editable && (
              <button className="btn small" onClick={() => setAod(aod.size === aodCats.length ? new Set() : new Set(aodCats.map((c) => c.id)))}>
                {aod.size === aodCats.length && aodCats.length ? 'Clear' : 'Select all'}
              </button>
            )}
          </span>
        }
      >
        <ChipPicker items={aodCats} selected={aod} onChange={setAod} editable={editable} />
      </Section>

      <Section
        icon={<Tv size={18} />}
        title="Live TV channels"
        sub="Channels included in this package."
        right={
          <span className="row">
            <span className="muted">
              <strong style={{ color: 'var(--ink)' }}>{chosen.size} selected</strong> (of {all.length})
            </span>
            {editable && (
              <>
                <button className="btn small" onClick={() => setChosen(new Set([...chosen, ...shown.map((c) => c.id)]))}>
                  Select all
                </button>
                <button className="btn small" onClick={() => setChosen(new Set([...chosen].filter((id) => !shown.some((c) => c.id === id))))}>
                  Clear
                </button>
              </>
            )}
          </span>
        }
      >
        <div className="filters">
          <label className="search">
            <Search size={16} />
            <input placeholder="Search channels by name, number or category…" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <Select value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category">
            <option value="all">All categories</option>
            <option value="selected">Only selected</option>
            {(categories.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <span className="view-toggle">
            <button type="button" className={view === 'grid' ? 'on' : ''} title="Grid" onClick={() => setView('grid')}>
              <LayoutGrid size={16} />
            </button>
            <button type="button" className={view === 'list' ? 'on' : ''} title="List" onClick={() => setView('list')}>
              <List size={16} />
            </button>
          </span>
        </div>
        {shown.length === 0 ? (
          <Empty>No channel matches.</Empty>
        ) : (
          <div className={view === 'grid' ? 'pick-grid' : 'pick-list'}>
            {rows.map((c) => {
              const prim = c.categories.find((x) => x.isPrimary) ?? c.categories[0];
              return (
                <label key={c.id} className={`pick-row${chosen.has(c.id) ? ' on' : ''}`}>
                  <input type="checkbox" disabled={!editable} checked={chosen.has(c.id)} onChange={() => toggle(c.id)} />
                  <span className="pick-num">{numberOf.get(c.id) ?? '—'}</span>
                  <span className="logo-tile">{c.logo ? <img src={mediaUrl(c.logo.storageKey)} alt="" /> : <span style={{ fontSize: 10, color: '#c4b5fd' }}>{c.name.slice(0, 3)}</span>}</span>
                  <span className="pick-name">{c.name}</span>
                  <CategoryChip category={prim?.category} />
                </label>
              );
            })}
          </div>
        )}
        <Pager {...pager} />
      </Section>
    </div>
  );
}

/** Packages for one scope (site / zone / room / device). Empty = inherit from the level above. */
export function AssignmentEditor({ siteId, scopeType, scopeId }: { siteId: string; scopeType: Assignment['scopeType']; scopeId: string }) {
  const qc = useQueryClient();
  const packages = usePackages();
  const current = useQuery({
    queryKey: ['assignments', siteId, scopeType, scopeId],
    queryFn: () => api.get<{ items: Assignment[] }>(`/sites/${siteId}/assignments?scopeType=${scopeType}&scopeId=${scopeId}`),
  });
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const selected = chosen ?? new Set((current.data?.items ?? []).map((a) => a.package.id));
  const save = useMutation({
    mutationFn: () => api.put('/assignments', { scopeType, scopeId, packageIds: [...selected] }),
    onSuccess: () => {
      setChosen(null);
      void qc.invalidateQueries({ queryKey: ['assignments'] });
      void qc.invalidateQueries({ queryKey: ['device'] });
    },
  });
  if (current.isLoading || packages.isLoading) return <Loading />;
  return (
    <div className="stack">
      <div className="muted">
        {selected.size === 0
          ? 'Nothing set here: this inherits from the level above (or the default package).'
          : 'These packages apply here and override anything set at a broader level.'}
      </div>
      <div className="row">
        {packages.data?.items.map((p) => {
          const on = selected.has(p.id);
          return (
            <button
              key={p.id}
              className={`btn small${on ? ' primary' : ''}`}
              onClick={() => {
                const next = new Set(selected);
                if (on) next.delete(p.id);
                else next.add(p.id);
                setChosen(next);
              }}
            >
              {p.name}
            </button>
          );
        })}
      </div>
      <ErrorBox error={save.error} />
      <div className="row">
        <button className="btn primary" disabled={chosen === null || save.isPending} onClick={() => save.mutate()}>
          Save
        </button>
        {save.isSuccess && <span className="badge ok">Saved · TVs update automatically</span>}
      </div>
    </div>
  );
}
